import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Tabs, TabsList, TabsTrigger } from '@/components/common/tabs'
import type { TabsConfig } from '@/components/opportunity/useTabsConfig'
import { useTxSuccessState } from '@/components/opportunity/useTxSuccessState'
import { routes } from '@/config/routes'
import { CROSSCHAIN_SUPPORTED_NETWORKS } from '@/constants/crosschain'
import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { useSound } from '@/context/sound'
import { useWalletType } from '@/hooks/useWalletType'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepName } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { AeraPendingTabTrigger, type AeraSpecificData } from '../actionTab/aeraPendingTab'
import { BoringWaitForRedeemTabTrigger } from '../actionTab/boringWaitForRedeemTab'
import { CLAIM_REWARDS_EXCEPTION_TAB_NAME } from '../actionTab/claimRewardsExceptionTab'
import { GenericPendingTabTrigger, hasPendingRequestsField, parsePendingRequests } from '../actionTab/genericPendingTab'
import { LIDO_WRAP_EXCEPTION_ADDRESS, LidoWrappingTabTrigger } from '../actionTab/lidoWrappingTab'
import type { MellowSpecificData } from '../actionTab/mellowDepositTab'
import { UmbrellaWithdrawTabTrigger } from '../actionTab/umbrellaWithdrawTab'
import { YoWaitForRedeemTabTrigger } from '../actionTab/yoWaitForRedeemTab'
import { NotEligibleNotice, isSyrupNotEligible } from './notEligibleNotice'
import { TxActionContent } from './txActionContent'

const REDIRECT_DELAY = 4 * SECOND_IN_MILLISECONDS

interface TxWidgetArgs {
  vault: Vault
  tabValue: StepName
  setTabValue: (value: StepName) => void
  transactionsContext: NonNullable<TransactionsContext>
  tabsConfig: TabsConfig
}

export const TxWidget = ({ vault, tabValue, setTabValue, transactionsContext, tabsConfig }: TxWidgetArgs) => {
  const navigate = useNavigate()
  const { account } = useAccountContext()
  const { play, hasSound } = useSound()
  const { asset, network, vaultId, address, protocol, rewards, protocolVaultUrl, transactionalProperties } = vault
  const [displayFundWallet, setDisplayFundWallet] = useState(false)
  const [isRedirectCancelled, setIsRedirectCancelled] = useState(false)
  const { trackEvent } = useMixpanel()
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasHandledSuccessRef = useRef(false)
  const { isEOA, isLoading: isWalletTypeLoading } = useWalletType(account)

  const isInstantDeposit = transactionalProperties?.depositStepsType === 'instant'
  const isCrosschainAllowed =
    asset.symbol === 'USDC' && CROSSCHAIN_SUPPORTED_NETWORKS.includes(network.name) && isInstantDeposit
  const isWalletReady = !isWalletTypeLoading
  const canShowCrosschain = isCrosschainAllowed && isWalletReady && isEOA

  const activeConfig = tabsConfig[tabValue]
  const { txData, resetTxState, setTransactionFinished } = useTxSuccessState()

  const isTxFinished = txData.finished
  const isTxLoading = activeConfig.form.formState.isSubmitting

  const txState = useMemo(() => {
    if (isTxLoading) return 'loading'
    if (isTxFinished) return 'success'
    return 'idle'
  }, [isTxLoading, isTxFinished])

  const isInternalClaimAvailable = transactionsContext?.allSteps.some(
    (item) => item.name === 'claim-redeem' || item.name === 'claim-rewards'
  )

  const isExternalClaimAvailable = Boolean(
    BigInt(transactionsContext?.asset.positionValueInAsset ?? 0n) > 0n &&
      protocolVaultUrl &&
      rewards.length > 0 &&
      !isInternalClaimAvailable
  )
  const isLidoException = protocol.name === 'lido' && address === LIDO_WRAP_EXCEPTION_ADDRESS
  const isBoringException =
    protocol.product === 'liquid boring vault' && (transactionsContext?.vaultSpecificData as any)?.redeemState
  const isUmbrellaException = protocol.product === 'umbrella'
  const isYoException = protocol.name === 'yo' && (transactionsContext?.vaultSpecificData as any)?.pendingRedeemRequest
  const isAeraException =
    protocol.name === 'aera' && (transactionsContext?.vaultSpecificData as AeraSpecificData)?.pendingRequests
  const isMellowException = protocol.name === 'mellow'
  const isNotEligibleException = isSyrupNotEligible(vault, transactionsContext)

  const hasGenericPending = hasPendingRequestsField(transactionsContext)
  const genericPendingRequests = parsePendingRequests(transactionsContext)
  const showGenericPendingTab = genericPendingRequests.length > 0

  const vaultLinkToHighlight = generatePath(routes.dashboard, { address: '' })

  const goToDashboard = useCallback(() => {
    navigate(vaultLinkToHighlight, { state: { vaultId, network: network.name, symbol: asset.symbol } })
  }, [navigate, vaultLinkToHighlight, vaultId, network.name, asset.symbol])

  const handleSuccess = useCallback(() => {
    if (hasHandledSuccessRef.current) return
    hasHandledSuccessRef.current = true

    if (hasSound('kaching')) {
      play('kaching')
    }

    setIsRedirectCancelled(false)
    redirectTimerRef.current = setTimeout(goToDashboard, REDIRECT_DELAY)
  }, [hasSound, play, goToDashboard])

  const cancelRedirect = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current)
      redirectTimerRef.current = null
    }
    setIsRedirectCancelled(true)
  }, [])

  useEffect(() => {
    if (isTxFinished) {
      handleSuccess()
    }

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [isTxFinished, handleSuccess])

  useEffect(() => {
    if (!isTxFinished) {
      hasHandledSuccessRef.current = false
      setIsRedirectCancelled(false)
    }
  }, [isTxFinished])

  return (
    <fieldset className="min-w-0">
      <Tabs
        className="group w-full"
        value={tabValue}
        onValueChange={(value) => {
          setTabValue(value as StepName)
          trackEvent('Opportunity widget tab changed', { Tab: tabsConfig[value as StepName].label })
        }}
      >
        <TabsList className={cn('w-full p-0', 'h-12', 'lg:h-14', txState !== 'idle' && 'hidden')}>
          <ScrollArea
            orientation="horizontal"
            type="auto"
            className={cn('h-full w-full', '[&_[data-role=scroll-area-viewport]>*]:h-full')}
          >
            <div className="flex h-full p-1 lg:p-2">
              {transactionsContext?.allSteps.map(({ name }) => {
                if (isUmbrellaException && umbrellaHiddenTabs.includes(name)) return null
                if (hasGenericPending && (name === 'claim-deposit' || name === 'claim-redeem')) return null
                if (isMellowException && name === 'claim-deposit') return null
                if (isMellowException && name === 'claim-redeem') {
                  const requestData = (transactionsContext?.vaultSpecificData as MellowSpecificData)?.requestData
                  if (!requestData?.redeemRequests?.length && !BigInt(requestData?.claimableRedeem ?? 0)) return null
                }

                const tabConfig = tabsConfig[name]
                return (
                  <TabsTrigger key={name} value={name} className={tabTriggerClassName}>
                    {tabConfig.label}
                  </TabsTrigger>
                )
              })}
              {isExternalClaimAvailable && (
                <TabsTrigger value={CLAIM_REWARDS_EXCEPTION_TAB_NAME} className={tabTriggerClassName}>
                  Claim
                </TabsTrigger>
              )}
              {isLidoException && <LidoWrappingTabTrigger className={tabTriggerClassName} />}
              {isBoringException && <BoringWaitForRedeemTabTrigger className={tabTriggerClassName} />}
              {isUmbrellaException && (
                <UmbrellaWithdrawTabTrigger transactionsContext={transactionsContext} className={tabTriggerClassName} />
              )}
              {isYoException && <YoWaitForRedeemTabTrigger className={tabTriggerClassName} />}
              {isAeraException && <AeraPendingTabTrigger className={tabTriggerClassName} />}
              {showGenericPendingTab && <GenericPendingTabTrigger className={tabTriggerClassName} />}
            </div>
            <ScrollBar orientation="horizontal" className="px-4 [&_[data-role=scroll-bar-thumb]]:bg-border" />
          </ScrollArea>
        </TabsList>

        {isNotEligibleException ? (
          transactionsContext.allSteps.map((step) => <NotEligibleNotice key={step.name} value={step.name} />)
        ) : (
          <TxActionContent
            account={account}
            vault={vault}
            transactionsContext={transactionsContext}
            tabsConfig={tabsConfig}
            activeConfig={activeConfig}
            txState={txState}
            txData={txData}
            resetTxState={resetTxState}
            setTransactionFinished={setTransactionFinished}
            isRedirectCancelled={isRedirectCancelled}
            cancelRedirect={cancelRedirect}
            goToDashboard={goToDashboard}
            displayFundWallet={displayFundWallet}
            setDisplayFundWallet={setDisplayFundWallet}
            canShowCrosschain={canShowCrosschain}
            exceptions={{
              isExternalClaimAvailable,
              isLidoException,
              isBoringException: !!isBoringException,
              isUmbrellaException,
              isYoException: !!isYoException,
              isAeraException: !!isAeraException,
              hasGenericPending,
              showGenericPendingTab,
              genericPendingRequests,
            }}
          />
        )}
      </Tabs>
    </fieldset>
  )
}

const umbrellaHiddenTabs: Array<keyof TabsConfig> = ['start-redeem-cooldown', 'claim-redeem']

const tabTriggerClassName = 'max-xxs:px-1 max-xxs:font-normal shrink-0'
